/**
 * FeesPaymentsSection - Shared component for Fees & Payments tab
 * 
 * Used by both AccreditationDashboard and ExaminationCenterDashboard
 * Handles pending payments, bank transfers, payment history, and invoices
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PaymentButton } from '@/components/PaymentButton';
import {
  CreditCard, FileText, Download, Building2, AlertCircle, Clock, CheckCircle, IndianRupee
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FeesPaymentsSection Component
 * 
 * @param {string} token - Auth token
 * @param {Array} pendingPayments - Array of pending payment items
 * @param {Function} onPaymentSuccess - Callback when payment succeeds
 * @param {Function} onBankTransferSubmit - Callback when bank transfer is submitted
 * @param {string} portalType - 'accreditation' or 'examination_center'
 * @param {number} discountRate - Discount rate (0.20 for accreditation, 0 for exam center)
 * @param {Function} getExamFee - Function to get exam fee by code
 * @param {Object} feeStructure - Fee structure object (optional)
 */
export function FeesPaymentsSection({
  token,
  pendingPayments = [],
  pendingVerification = [],
  onPaymentSuccess,
  onBankTransferSubmit,
  portalType = 'examination_center',
  discountRate = 0,
  getExamFee,
  feeStructure = null
}) {
  const [paymentHistory, setPaymentHistory] = useState({ payments: [], total_paid: 0 });
  const [bankDetails, setBankDetails] = useState(null);
  const [showBankTransferModal, setShowBankTransferModal] = useState(null);
  const [bankTransferForm, setBankTransferForm] = useState({
    transfer_reference: '',
    transfer_date: '',
    bank_name: '',
    amount: 0,
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // API base path based on portal type
  const apiBasePath = portalType === 'accreditation' 
    ? `${API}/accreditation` 
    : `${API}/examination-center`;

  // Fetch payment history
  const fetchPaymentHistory = async () => {
    try {
      const res = await axios.get(`${apiBasePath}/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
    }
  };

  // Fetch bank details
  const fetchBankDetails = async () => {
    try {
      const endpoint = portalType === 'accreditation' 
        ? `${API}/accreditation/bank-details`
        : `${API}/examination-center/bank-details`;
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch bank details:', err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPaymentHistory();
      fetchBankDetails();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle bank transfer submission
  const handleBankTransferSubmit = async (item) => {
    if (!bankTransferForm.transfer_reference || !bankTransferForm.transfer_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await onBankTransferSubmit?.(item.id, {
        ...bankTransferForm,
        amount: item.fee || item.base_fee || bankTransferForm.amount
      });
      
      toast.success('Bank transfer details submitted. Awaiting admin verification.');
      setShowBankTransferModal(null);
      setBankTransferForm({
        transfer_reference: '',
        transfer_date: '',
        bank_name: '',
        amount: 0,
        remarks: ''
      });
      fetchPaymentHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bank transfer');
    } finally {
      setSubmitting(false);
    }
  };

  // Download invoice
  const downloadInvoice = async (recordId, invoiceType = 'auto') => {
    try {
      const endpoint = portalType === 'accreditation'
        ? `${API}/accreditation/generate-invoice/${recordId}`
        : `${API}/examination-center/generate-invoice/${recordId}?invoice_type=${invoiceType}`;
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${recordId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  // Calculate fee with discount
  const calculateFee = (item) => {
    const baseFee = item.base_fee || item.fee || (getExamFee ? getExamFee(item.examination, feeStructure) : 0);
    return discountRate > 0 ? Math.round(baseFee * (1 - discountRate)) : baseFee;
  };

  // Get payment status badge
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      pending_payment: 'bg-amber-100 text-amber-700',
      pending_verification: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      completed: 'bg-green-100 text-green-700',
      verified: 'bg-green-100 text-green-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6">
      {/* Pending Payments Section */}
      {pendingPayments.length > 0 && (
        <Card data-testid="pending-payments-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Pending Student Payments
            </CardTitle>
            <p className="text-sm text-slate-500">Collect examination fees for nominated students</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingPayments.map((item) => {
                const fee = calculateFee(item);
                return (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-[#1B2A4A]">{item.full_name}</p>
                        <Badge className="bg-[#1B2A4A] text-white text-xs">{item.examination}</Badge>
                      </div>
                      <p className="text-sm text-slate-500">{item.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {discountRate > 0 && getExamFee && (
                          <p className="text-xs text-slate-400 line-through">
                            ₹{getExamFee(item.examination, feeStructure)?.toLocaleString()}
                          </p>
                        )}
                        <p className="text-lg font-bold text-[#1B2A4A]">₹{fee.toLocaleString()}</p>
                      </div>
                      <PaymentButton
                        amount={fee}
                        description={`${item.examination} Exam Fee - ${item.full_name}`}
                        onSuccess={(paymentId) => onPaymentSuccess?.(paymentId, item)}
                        onError={(error) => toast.error(error || 'Payment failed')}
                        className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm"
                        data-testid={`pay-btn-${item.id}`}
                      >
                        Pay ₹{fee.toLocaleString()}
                      </PaymentButton>
                      <Button
                        variant="outline"
                        className="rounded-sm"
                        onClick={() => {
                          setShowBankTransferModal(item);
                          setBankTransferForm(prev => ({ ...prev, amount: fee }));
                        }}
                        data-testid={`bank-transfer-btn-${item.id}`}
                      >
                        Bank Transfer
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Verification Section */}
      {pendingVerification.length > 0 && (
        <Card className="border-l-4 border-l-blue-500" data-testid="pending-verification-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" /> Pending Verification
            </CardTitle>
            <p className="text-sm text-slate-500">Bank transfers awaiting admin confirmation</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingVerification.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#1B2A4A]">{item.full_name}</p>
                      <Badge className="bg-blue-500 text-white text-xs">{item.examination}</Badge>
                    </div>
                    <p className="text-sm text-slate-500">{item.email}</p>
                    {item.bank_transfer_reference && (
                      <p className="text-xs text-blue-600 mt-1">Ref: {item.bank_transfer_reference}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-blue-600">₹{(item.base_fee || item.fee)?.toLocaleString()}</p>
                    <p className="text-xs text-blue-500">Awaiting admin verification</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card data-testid="payment-history-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" /> Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {paymentHistory.payments?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F8FAFC]">
                    <TableHead className="text-xs font-bold">Description</TableHead>
                    <TableHead className="text-xs font-bold">Type</TableHead>
                    <TableHead className="text-xs font-bold">Date</TableHead>
                    <TableHead className="text-xs font-bold">Payment ID</TableHead>
                    <TableHead className="text-xs font-bold">Method</TableHead>
                    <TableHead className="text-xs font-bold text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold">Invoice</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentHistory.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="text-sm">{payment.description}</TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700 text-xs">{payment.type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {payment.date ? new Date(payment.date).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-slate-500">{payment.payment_id}</TableCell>
                      <TableCell>
                        <Badge className={payment.payment_method === 'bank_transfer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {payment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 'Razorpay'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-right text-green-600">
                        ₹{payment.amount?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs rounded-sm"
                          onClick={() => downloadInvoice(payment.enrollment_id, payment.invoice_type)}
                          data-testid={`invoice-btn-${payment.id}`}
                        >
                          <Download className="h-3 w-3 mr-1" /> Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 p-4 bg-[#1B2A4A] text-white rounded-sm flex justify-between items-center">
                <span className="text-sm">Total Paid</span>
                <span className="text-xl font-bold">₹{paymentHistory.total_paid?.toLocaleString()}</span>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No payment history yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bank Details Card */}
      {bankDetails && (
        <Card className="border-l-4 border-l-[#1B2A4A]" data-testid="bank-details-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" /> IMCAC Bank Details for Transfer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Bank Name</p>
                <p className="font-medium">{bankDetails.bank_name}</p>
              </div>
              <div>
                <p className="text-slate-500">Branch</p>
                <p className="font-medium">{bankDetails.branch}</p>
              </div>
              <div>
                <p className="text-slate-500">Account Name</p>
                <p className="font-medium">{bankDetails.account_name}</p>
              </div>
              <div>
                <p className="text-slate-500">Account Number</p>
                <p className="font-medium font-mono">{bankDetails.account_number}</p>
              </div>
              <div>
                <p className="text-slate-500">IFSC Code</p>
                <p className="font-medium font-mono">{bankDetails.ifsc_code}</p>
              </div>
              <div>
                <p className="text-slate-500">UPI ID</p>
                <p className="font-medium font-mono">{bankDetails.upi_id}</p>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-4 p-2 bg-amber-50 rounded">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              Please include Student Name and Examination Code in payment remarks
            </p>
          </CardContent>
        </Card>
      )}

      {/* Bank Transfer Modal */}
      {showBankTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4">
            <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
              <CardTitle className="text-base">Submit Bank Transfer Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-sm">
                  <p className="font-semibold text-[#1B2A4A]">{showBankTransferModal.full_name}</p>
                  <p className="text-sm text-slate-500">{showBankTransferModal.examination} Examination</p>
                  <p className="text-lg font-bold text-[#C5A572] mt-2">
                    ₹{(showBankTransferModal.base_fee || showBankTransferModal.fee)?.toLocaleString()}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Transfer Reference/UTR *</Label>
                    <Input
                      placeholder="Enter UTR or reference number"
                      value={bankTransferForm.transfer_reference}
                      onChange={(e) => setBankTransferForm(prev => ({ ...prev, transfer_reference: e.target.value }))}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Transfer Date *</Label>
                    <Input
                      type="date"
                      value={bankTransferForm.transfer_date}
                      onChange={(e) => setBankTransferForm(prev => ({ ...prev, transfer_date: e.target.value }))}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Bank Name</Label>
                    <Input
                      placeholder="Your bank name"
                      value={bankTransferForm.bank_name}
                      onChange={(e) => setBankTransferForm(prev => ({ ...prev, bank_name: e.target.value }))}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Remarks</Label>
                    <Input
                      placeholder="Any additional notes"
                      value={bankTransferForm.remarks}
                      onChange={(e) => setBankTransferForm(prev => ({ ...prev, remarks: e.target.value }))}
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowBankTransferModal(null)} 
                    className="flex-1 rounded-sm"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => handleBankTransferSubmit(showBankTransferModal)}
                    className="flex-1 bg-[#1B2A4A] hover:bg-[#142238] text-white rounded-sm"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Transfer'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default FeesPaymentsSection;
